import { Col, Row } from "antd";
import { ReactNode } from "react";

import styles from "./styles.module.scss";
import { getDefaultCookiePolicyStorage } from "./policyData";

const Section = ({
	number,
	title,
	children,
}: {
	number: number;
	title: string;
	children: ReactNode;
}) => (
	<>
		<Row className={styles.row}>
			<Col xs={3} sm={2} className={styles.bold}>
				{number}
			</Col>
			<Col xs={21} sm={22} className={styles.bold}>
				{title}
			</Col>
		</Row>
		<Row className={styles.row}>
			<Col xs={3} sm={2} />
			<Col xs={21} sm={22} className={styles.normal}>
				{children}
			</Col>
		</Row>
	</>
);

const DefaultCookiePolicy = () => (
	<>
		<Row className={styles.row}>
			<Col span={24} className={styles.policyDate}>
				Effective date: To be confirmed
				<br />
				Last updated: To be confirmed
			</Col>
		</Row>
		<Section number={1} title="Introduction">
			<p>
				<strong>1.1</strong> These terms govern your use of our website
				at{" "}
				<a
					href="https://www.ugenie.io"
					target="_blank"
					rel="noreferrer"
				>
					www.ugenie.io
				</a>
				, any associated websites, mobile applications, white-labelled
				subdomains or branded platforms operated on behalf of our
				customers, and the services provided through them (together, the
				“Platform”).
			</p>
			<p>
				<strong>1.2</strong> “We”, “us” and “our” refer to Ugenie Ltd,
				the owner and operator of the Platform. Our registered office is
				at 5 Gunnersbury Crescent, London, W3 9AA, and our company
				registration number is 09966790.
			</p>
			<p>
				<strong>1.3</strong> “You” refers to any user or viewer of the
				Platform, including any member of a community created through
				the Ugenie app or a white-labelled version of the Platform
				operated on behalf of a Ugenie customer (a “Community”).
			</p>
			<p>
				<strong>1.4</strong> By continuing to use the Platform, you
				agree to this Cookie Policy. If you disagree with any part of
				it, you must not use the Platform.
			</p>
			<p>
				<strong>1.5</strong> The Platform uses cookies and similar
				tracking technologies. We will request your explicit consent
				before placing any non-essential cookies on your device. You can
				manage your cookie preferences at any time through our Cookie
				Settings tool or through your browser settings.
			</p>
			<p>
				<strong>1.6</strong> You must be at least 18 years of age to use
				the Platform. The Platform is not intended for users under 18.
				We encourage parents and guardians to supervise their children’s
				online activity and to use appropriate parental control tools.
			</p>
		</Section>
		<Section number={2} title="What are cookies?">
			<p>
				Cookies are small text files that are stored on your device when
				you visit a website. They allow a site to recognise your device
				and, depending on the type of cookie, either keep the Platform
				running securely or help us understand and improve how it is
				used. Similar technologies, such as pixels and local storage,
				are covered by this policy wherever they perform an equivalent
				function.
			</p>
		</Section>
		<Section number={3} title="Types of cookies we use">
			<p>
				The table below sets out the cookies and similar storage used on
				the Platform, what each is for, and how long each stays on your
				device. Where we introduce analytics or functionality cookies in
				the future, we will update this table and, where required, ask
				for your consent before they are set.
			</p>
			<div className={styles.tableContainer}>
				<table>
					<thead>
						<tr>
							<th>Category</th>
							<th>Purpose</th>
							<th>Example cookie or storage names</th>
							<th>Duration</th>
							<th>First / third party</th>
						</tr>
					</thead>
					<tbody>
						{getDefaultCookiePolicyStorage().map((cookie) => (
							<tr key={cookie.category}>
								<td>{cookie.category}</td>
								<td>{cookie.purpose}</td>
								<td>{cookie.names}</td>
								<td>{cookie.duration}</td>
								<td>{cookie.party}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</Section>
		<Section number={4} title="Our legal basis for using cookies">
			<ul>
				<li>
					Essential cookies, including those set by Stripe for payment
					security and fraud prevention, and our cc_cookie which
					records your consent choices, are used on the basis of our
					legitimate interests in operating the Platform securely,
					reliably and efficiently. These cookies cannot be switched
					off, as the Platform will not function correctly without
					them.
				</li>
				<li>
					Any analytics or functionality cookies we introduce in
					future will be used only where you have given your explicit
					consent. You are free to withhold or withdraw that consent
					at any time, and doing so will not affect your ability to
					use the core features of the Platform.
				</li>
			</ul>
		</Section>
		<Section number={5} title="Managing your cookies">
			<p>
				You are in control of the non-essential cookies placed on your
				device. You can:
			</p>
			<ul>
				<li>
					Give or withdraw consent at any time using our Cookie Banner
					or Cookie Preference Centre.
				</li>
				<li>
					Adjust your browser settings to block, restrict or delete
					cookies. Every browser is different, so check the “Help”
					menu of your browser to learn how to change your cookie
					preferences.
				</li>
			</ul>
			<p>
				Please note that blocking or deleting certain cookies,
				particularly essential cookies, may affect the functionality of
				the Platform.
			</p>
		</Section>
		<Section number={6} title="Use on white-labelled platforms">
			<p>
				Where you access a white-labelled version of the Platform
				operated by a Ugenie customer, cookies and tracking technologies
				may still be set by Ugenie, since we provide the underlying
				platform infrastructure. This Cookie Policy continues to apply
				in those cases. Our customers may provide their own additional
				privacy or cookie notices for their Community, but any cookies
				controlled by Ugenie will always be governed by this policy.
			</p>
		</Section>
		<Section number={7} title="Changes to this policy">
			<p>
				We may update this Cookie Policy from time to time to reflect
				changes in the cookies we use or for other operational, legal or
				regulatory reasons. Where we make material changes, we will let
				you know via the Platform or by email.
			</p>
		</Section>
		<Section number={8} title="Contact us">
			<p>
				If you have any questions about this Cookie Policy or how we use
				cookies, please contact us at{" "}
				<a href="mailto:info@ugenie.io">info@ugenie.io</a>.
			</p>
		</Section>
	</>
);

const CookiePolicy = ({
	data,
	communityName,
}: {
	data?: string;
	communityName?: string;
}) => (
	<div className={styles.content}>
		<div className={styles.title}>{communityName || "UGENIE"}</div>
		<div className={styles.subTitle}>PLATFORM COOKIE POLICY</div>
		{data ? (
			<Row className={styles.row}>
				<Col span={24} className={styles.normal}>
					<div dangerouslySetInnerHTML={{ __html: data }} />
				</Col>
			</Row>
		) : (
			<DefaultCookiePolicy />
		)}
	</div>
);

export default CookiePolicy;
